// OffboardingView — the offboarding task body, actioned per-account in the
// drawer: a leave-date / accounts-to-remove KPI strip, the leaver's person
// card, and the Admin-transfer / Account-removal sub-tabs. Each row is handled
// independently (mirroring the original portal): removal rows can be Marked
// removed or Declined (with a reason); admin rows can be Transferred or marked
// Not required. All wiring lives in the shared `useOffboardingChecklist`
// controller — there is no whole-task Save/Refuse.

import { Box, Flex, Grid, HStack, Icon, Input, Text } from "@chakra-ui/react";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { useState } from "react";
import { TbArrowsExchange, TbBan, TbCheck, TbX } from "react-icons/tb";

import AppLogo from "components/ds/AppLogo";
import Chip from "components/ds/Chip";
import SectionHeader from "components/ds/SectionHeader";
import SubTabs from "components/ds/SubTabs";
import UtilityButton from "components/ds/UtilityButton";
import type { MFOffboardingDetail } from "state/members/tasks";

import PersonCard from "./PersonCard";
import type {
  OffChecklistItem,
  OffboardingChecklistController,
  OffGroupKey,
} from "./useOffboardingChecklist";

// "May 17, 2026" (with comma, matching the KPI design — distinct from the
// `taskMeta.formatDate` "MMM d yyyy" used elsewhere).
const formatLeaveDate = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return null;
  }
};

const daysLeftLabel = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    const diff = differenceInCalendarDays(parseISO(value), new Date());
    if (diff > 1) return `${diff} days left`;
    if (diff === 1) return "1 day left";
    if (diff === 0) return "Today";
    return `${Math.abs(diff)} days ago`;
  } catch {
    return null;
  }
};

const KpiCell = ({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string | null;
  tone?: "red";
}) => (
  <Box borderWidth="1px" borderColor="neutral.200" borderRadius="10px" bg="neutral.25" px="14px" py="md">
    <Text fontSize="xs" color="neutral.700" mb="2xs">
      {label}
    </Text>
    <Text
      fontFamily="heading"
      fontWeight={600}
      fontSize={tone === "red" ? "lg" : "xl"} // leave date 16px, count 18px → xl≈18 / lg=16
      color={tone === "red" ? "error.700" : "neutral.950"}
      lineHeight="1.2"
    >
      {value}
    </Text>
    {sub && (
      <Text fontSize="xs" color={tone === "red" ? "error.700" : "neutral.700"} mt="2px">
        {sub}
      </Text>
    )}
  </Box>
);

export interface OffboardingViewProps {
  detail: MFOffboardingDetail;
  controller: OffboardingChecklistController;
}

const OffboardingView = ({ detail, controller }: OffboardingViewProps) => {
  const [tab, setTab] = useState<OffGroupKey>("removal");

  const leaveDate = formatLeaveDate(detail.leaveDate);
  const daysLeft = daysLeftLabel(detail.leaveDate);
  const total = detail.accountsTotal ?? detail.accounts.length;
  const remaining = detail.accountsRemaining ?? controller.remaining;

  const activeGroup =
    controller.groups.find((g) => g.key === tab) ?? controller.groups[0];

  const anyBusy = Boolean(controller.busyId);

  // The trailing control(s) for one row, by group + state.
  const RowActions = ({ item }: { item: OffChecklistItem }) => {
    // While declining, the reason block (below) owns this row.
    if (controller.decliningId === item.accountId) return null;

    if (item.state === "declined")
      return (
        <Chip tone="error" icon={<Icon as={TbX} boxSize="12px" />}>
          Declined
        </Chip>
      );

    // Nobody refused this one and nobody has to do it — an admin called the
    // deprovisioning off, so it is resolved without being anyone's success or
    // failure. Neutral tone, no action buttons.
    if (item.state === "cancelled")
      return (
        <Chip tone="default" icon={<Icon as={TbX} boxSize="12px" />}>
          Cancelled
        </Chip>
      );

    if (item.state === "done")
      return (
        <Chip tone="success" icon={<Icon as={TbCheck} boxSize="12px" />}>
          {activeGroup.key === "removal" ? "Removed" : "Done"}
        </Chip>
      );

    const busy = controller.busyId === item.accountId;

    if (activeGroup.key === "removal")
      return (
        <HStack spacing="sm" flexShrink={0}>
          <UtilityButton
            size="md"
            treatment="dropshadow"
            sentiment="positive"
            leftIcon={<Icon as={TbCheck} boxSize="14px" />}
            isLoading={busy}
            isDisabled={anyBusy}
            onClick={() => controller.markRemoved(item.accountId)}
          >
            Mark removed
          </UtilityButton>
          <UtilityButton
            size="md"
            treatment="subtle"
            sentiment="negative"
            leftIcon={<Icon as={TbBan} boxSize="14px" />}
            isDisabled={anyBusy}
            onClick={() => controller.startDecline(item.accountId)}
          >
            Decline
          </UtilityButton>
        </HStack>
      );

    return (
      <HStack spacing="sm" flexShrink={0}>
        <UtilityButton
          size="md"
          treatment="dropshadow"
          sentiment="positive"
          leftIcon={<Icon as={TbArrowsExchange} boxSize="14px" />}
          isLoading={busy}
          isDisabled={anyBusy}
          onClick={() => controller.markAdminTransferred(item.accountId)}
        >
          Transferred
        </UtilityButton>
        <UtilityButton
          size="md"
          treatment="subtle"
          isDisabled={anyBusy}
          onClick={() => controller.markAdminNotRequired(item.accountId)}
        >
          Not required
        </UtilityButton>
      </HStack>
    );
  };

  return (
    <>
      {/* KPI strip — leave date + accounts to remove */}
      {(leaveDate || total > 0) && (
        <Grid templateColumns="1fr 1fr" gap="md">
          <KpiCell
            label="Leave date"
            value={leaveDate ?? "Not set"}
            sub={daysLeft ? `(${daysLeft})` : null}
            tone="red"
          />
          <KpiCell
            label="Accounts to remove"
            value={String(remaining)}
            sub={`of ${total} total`}
          />
        </Grid>
      )}

      <Box>
        <SectionHeader>Employee</SectionHeader>
        <PersonCard
          member={detail.offboardingMember}
          showHrContext
          showStarted={false}
          extraChips={
            leaveDate ? (
              <Chip dot="error">
                <Box as="span" fontSize="md" color="neutral.950">
                  Leaving {leaveDate}
                </Box>
              </Chip>
            ) : undefined
          }
        />
      </Box>

      {controller.hasAccounts && activeGroup && (
        <Box>
          <SubTabs
            size="sm"
            activeKey={tab}
            onChange={(k) => setTab(k as OffGroupKey)}
            tabs={controller.groups.map((g) => ({
              key: g.key,
              label: g.label,
              count: `${g.resolvedCount}/${g.total}`,
            }))}
          />
          <Flex direction="column" gap="sm" mt="md">
            {activeGroup.items.map((item) => {
              const declining = controller.decliningId === item.accountId;
              const resolved = item.state !== "pending";
              return (
                <Box
                  key={item.accountId}
                  borderWidth="1px"
                  borderColor={declining ? "error.200" : "neutral.200"}
                  borderRadius="10px"
                  bg="neutral.25"
                  px="14px"
                  py="md"
                >
                  <Flex align="center" gap="md">
                    <AppLogo
                      name={item.appName}
                      logoUrl={item.appImageUrl ?? undefined}
                      size="sm"
                    />
                    <Box
                      flex="1"
                      minW={0}
                      fontSize="smd"
                      fontWeight={500}
                      color={resolved ? "neutral.500" : "neutral.900"}
                      textDecoration={
                        item.state === "done" ? "line-through" : "none"
                      }
                      noOfLines={1}
                    >
                      {item.appName}
                    </Box>
                    <RowActions item={item} />
                  </Flex>

                  {declining && (
                    <Box mt="md" pt="md" borderTopWidth="1px" borderColor="neutral.100">
                      <Text fontSize="xs" color="neutral.700" mb="2xs">
                        Why can't this account be removed? IT will see this.
                      </Text>
                      <Flex gap="sm" align="center">
                        <Input
                          size="sm"
                          autoFocus
                          borderRadius="component.md"
                          value={controller.declineReason}
                          onChange={(e) => controller.setDeclineReason(e.target.value)}
                          placeholder="Reason for declining…"
                        />
                        <UtilityButton
                          size="md"
                          treatment="dropshadow"
                          sentiment="negative"
                          isLoading={controller.busyId === item.accountId}
                          isDisabled={!controller.canConfirmDecline || anyBusy}
                          onClick={controller.confirmDecline}
                        >
                          Confirm
                        </UtilityButton>
                        <UtilityButton
                          size="md"
                          treatment="subtle"
                          isDisabled={anyBusy}
                          onClick={controller.cancelDecline}
                        >
                          Cancel
                        </UtilityButton>
                      </Flex>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}
    </>
  );
};

export default OffboardingView;
